import mixpanel from 'mixpanel-browser';
import { logError, logInfo, logWarn } from '@/lib/logger';

// Extend the Window interface to include our custom property
declare global {
  interface Window {
    mixpanel_device_id?: string;
    __mp_init_started__?: boolean;
    __mp_init_done__?: boolean;
  }
}

let initStarted = false;
let initDone = false;

const CAMPAIGN_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_officer',
  'utm_referrer',
] as const;

type CampaignParamKey = typeof CAMPAIGN_PARAM_KEYS[number];

interface CampaignTouch extends Partial<Record<CampaignParamKey, string>> {
  landing_page?: string;
  referrer?: string;
  referring_domain?: string;
  captured_at?: string;
}

interface CampaignContext {
  first_touch: CampaignTouch;
  last_touch: CampaignTouch;
}

const CAMPAIGN_STORAGE_KEY = 'mixpanel_campaign_context';
const USER_STATUS_STORAGE_KEY = 'mixpanel_user_status';
const DEFAULT_USER_STATUS = 'anonymous';

let cachedCampaignContext: CampaignContext | null = null;

const safeJsonParse = <T>(value: string | null): T | null => {
  if (!value) {return null;}
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const readCampaignContext = (): CampaignContext | null => {
  if (typeof window === 'undefined') {return cachedCampaignContext;}
  try {
    const raw = window.sessionStorage?.getItem(CAMPAIGN_STORAGE_KEY) ?? null;
    const parsed = safeJsonParse<CampaignContext>(raw);
    if (parsed) {
      cachedCampaignContext = parsed;
      return parsed;
    }
  } catch {
    // Ignore storage access issues (Safari private mode, etc.)
  }
  return cachedCampaignContext;
};

const saveCampaignContext = (context: CampaignContext) => {
  if (typeof window === 'undefined') {return;}
  try {
    window.sessionStorage?.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Ignore storage write issues
  }
};

const getReferringDomain = (referrer: string | undefined): string | undefined => {
  if (!referrer || referrer === 'direct') {return referrer;}
  try {
    const url = new URL(referrer);
    return url.hostname;
  } catch {
    return undefined;
  }
};

const collectCampaignTouchFromUrl = (): CampaignTouch | null => {
  if (typeof window === 'undefined') {return null;}
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    let hasParam = false;
    const touch: CampaignTouch = {
      captured_at: new Date().toISOString(),
    };

    CAMPAIGN_PARAM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        touch[key] = value;
        hasParam = true;
      }
    });

    if (!hasParam) {
      return null;
    }

    touch.landing_page = `${url.pathname}${url.search}`;
    const referrer = document?.referrer || 'direct';
    touch.referrer = referrer || 'direct';
    touch.referring_domain = getReferringDomain(referrer) || 'direct';

    return touch;
  } catch {
    return null;
  }
};

const ensureCampaignContext = (): { context: CampaignContext | null; capturedFresh: boolean } => {
  if (typeof window === 'undefined') {
    return { context: cachedCampaignContext, capturedFresh: false };
  }

  const existing = readCampaignContext();
  const newTouch = collectCampaignTouchFromUrl();

  if (!newTouch) {
    cachedCampaignContext = existing;
    return { context: existing, capturedFresh: false };
  }

  const context: CampaignContext = existing
    ? { first_touch: existing.first_touch, last_touch: newTouch }
    : { first_touch: newTouch, last_touch: newTouch };

  cachedCampaignContext = context;
  saveCampaignContext(context);

  return { context, capturedFresh: true };
};

const buildPrefixedProps = (touch: CampaignTouch, prefix = ''): Record<string, string> => {
  const props: Record<string, string> = {};
  CAMPAIGN_PARAM_KEYS.forEach((key) => {
    const value = touch[key];
    if (value) {
      props[`${prefix}${key}`] = value;
    }
  });

  if (touch.landing_page) {
    props[`${prefix}landing_page`] = touch.landing_page;
  }
  if (touch.referrer) {
    props[`${prefix}referrer`] = touch.referrer;
  }
  if (touch.referring_domain) {
    props[`${prefix}referring_domain`] = touch.referring_domain;
  }
  if (touch.utm_campaign) {
    props[`${prefix}campaign_name`] = touch.utm_campaign;
  }
  if (touch.utm_officer) {
    props[`${prefix}campaign_officer`] = touch.utm_officer;
  }

  return props;
};

const registerCampaignSuperProps = (mp: typeof mixpanel, context: CampaignContext | null) => {
  if (!context) {return;}

  const initialProps = buildPrefixedProps(context.first_touch, 'initial_');
  if (context.first_touch.referrer) {
    initialProps.$initial_referrer = context.first_touch.referrer;
    initialProps.$initial_referring_domain = context.first_touch.referring_domain || 'direct';
  }

  const currentProps = buildPrefixedProps(context.last_touch);
  if (context.last_touch.referrer) {
    currentProps.$referrer = context.last_touch.referrer;
    currentProps.$referring_domain = context.last_touch.referring_domain || 'direct';
  }

  try {
    mp.register_once(initialProps);
    mp.register(currentProps);
  } catch {
    // Ignore register errors
  }
};

const getCampaignEventProps = (): Record<string, string> => {
  const context = cachedCampaignContext || readCampaignContext();
  if (!context) {return {};}

  const baseProps = buildPrefixedProps(context.last_touch);
  const initialProps = buildPrefixedProps(context.first_touch, 'initial_');

  return {
    ...initialProps,
    ...baseProps,
  };
};

const withCampaignProps = (props?: Record<string, unknown>) => ({
  ...getCampaignEventProps(),
  ...(props || {}),
});

const applyCampaignAttribution = (mp: typeof mixpanel) => {
  const { context, capturedFresh } = ensureCampaignContext();
  registerCampaignSuperProps(mp, context);

  if (capturedFresh && context?.last_touch) {
    try {
      mp.track('Campaign Attribution Captured', {
        ...buildPrefixedProps(context.last_touch),
        flow: 'last_touch',
      });
    } catch {
      // Ignore tracking failures
    }
  }
};

const getStoredUserStatus = (): string | null => {
  if (typeof window === 'undefined') {return null;}
  try {
    return window.sessionStorage?.getItem(USER_STATUS_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
};

const setStoredUserStatus = (status: string) => {
  if (typeof window === 'undefined') {return;}
  try {
    window.sessionStorage?.setItem(USER_STATUS_STORAGE_KEY, status);
  } catch {
    // Ignore storage issues
  }
};

const ensureAnonymousUserStatus = (mp: typeof mixpanel) => {
  const existingStatus = getStoredUserStatus();
  const statusToUse = existingStatus || DEFAULT_USER_STATUS;

  try {
    mp.register({ user_status: statusToUse });
  } catch {
    // Ignore register issues
  }

  if (!existingStatus) {
    setStoredUserStatus(statusToUse);
  }
};

export const getCampaignContext = (): CampaignContext | null => {
  return cachedCampaignContext || readCampaignContext();
};

export const isMixpanelReady = (): boolean => {
  if (typeof window === 'undefined') {return false;}
  return !!(initDone || window.__mp_init_done__);
};

const applyAnalyticsEnvTag = (mp: { register: (props: Record<string, unknown>) => void }) => {
  const envTag = process.env.NEXT_PUBLIC_MIXPANEL_ENV_TAG?.trim()
  if (!envTag) {
    return
  }

  try {
    mp.register({ analytics_env_tag: envTag })
  } catch {
    // Ignore register issues
  }
};

// Initialize Mixpanel with Simplified ID Merge
export const initMixpanel = () => {
  // Check if we're in browser environment
  if (typeof window === 'undefined') {
    return null;
  }

  // Prevent duplicate init (dev HMR / double mounts)
  if (initStarted || window.__mp_init_started__) {
    return mixpanel;
  }
  initStarted = true;
  window.__mp_init_started__ = true;

  const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
  
  if (!token) {
    logWarn('Mixpanel token not found. Analytics will be disabled.');
    return null;
  }

  try {
    mixpanel.init(token, {
      debug: process.env.NODE_ENV === 'development',
      track_pageview: false, // We'll handle this manually
      persistence: 'localStorage',
      // Simplified ID Merge configuration
      api_host: 'https://api.mixpanel.com',
      // Reduce queue pressure to prevent mutex lock timeouts
      batch_requests: true,
      batch_size: 10,
      batch_flush_interval_ms: 1000,
      // Ensure proper ID management
      loaded: (mp) => {
        // Mark as initialized
        initDone = true;
        window.__mp_init_done__ = true;

        // Get the current distinct_id (which will be $device_id for anonymous users)
        const currentDistinctId = mp.get_distinct_id();
        logInfo('Mixpanel initialized', { distinctId: currentDistinctId });

        // Store the device ID for reference
        if (typeof window !== 'undefined') {
          window.mixpanel_device_id = currentDistinctId;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ensureAnonymousUserStatus(mp as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        applyAnalyticsEnvTag(mp as any);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        applyCampaignAttribution(mp as any);
      }
    });
    return mixpanel;
  } catch (error) {
    logError('Failed to initialize Mixpanel', error);
    return null;
  }
};

// Get Mixpanel instance
export const getMixpanel = () => {
  if (typeof window === 'undefined') {
    return null; // Server-side rendering
  }
  
  return mixpanel;
};

// Track page views with Simplified ID Merge
export const trackPageView = (
  pageName: string, 
  properties?: Record<string, unknown>,
  options?: { pageType?: 'landing_page' | 'destination' | 'conversion' | 'standard' }
) => {
  try {
    if (!isMixpanelReady()) {
      // Not ready; skip to avoid SDK internal errors during init
      return;
    }
    const mp = getMixpanel();
    if (mp && typeof mp.track === 'function') {
      mp.track('Page View', withCampaignProps({
        page: pageName,
        page_name: pageName,
        page_type: options?.pageType || 'standard',
        // Simplified ID Merge: $device_id is automatically set by Mixpanel
        // $user_id will be set when user is identified
        ...properties,
      }));
    } else {
      logWarn('Mixpanel not initialized or track method not available');
    }
  } catch (error) {
    logError('Failed to track page view', error);
  }
};

// Event throttling to prevent mutex lock timeouts
const eventThrottle = new Map<string, number>();
const THROTTLE_DELAY = 100; // 100ms between same events

// Track custom events with Simplified ID Merge
export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  try {
    if (!isMixpanelReady()) {
      return;
    }

    // Throttle rapid-fire events. Consent updates in one click share an event
    // name but differ by agreement type, so those are throttled separately.
    const now = Date.now();
    const agreementType = properties?.agreement_type;
    const action = properties?.action;
    const throttleKey = typeof agreementType === 'string' && typeof action === 'string'
      ? `${eventName}:${action}:${agreementType}`
      : eventName;
    const lastSent = eventThrottle.get(throttleKey) || 0;
    if (now - lastSent < THROTTLE_DELAY) {
      return;
    }
    eventThrottle.set(throttleKey, now);

    const mp = getMixpanel();
    if (mp) {
      // Simplified ID Merge: $device_id and $user_id are automatically managed by Mixpanel
      // The distinct_id will be determined by Mixpanel based on these properties
      mp.track(eventName, withCampaignProps(properties));
    }
  } catch (error) {
    logError('Failed to track event', error);
    
    // If we get a mutex lock timeout, try to clear the queue
    if (error instanceof Error && error.message && error.message.includes('mutex')) {
      logWarn('Mutex lock detected, attempting to clear Mixpanel queue');
      try {
        const mp = getMixpanel();
        if (mp && typeof mp === 'object' && 'flush' in mp) {
          // Force flush any pending events
          (mp as { flush?: () => void }).flush?.();
        }
      } catch (flushError) {
        logError('Failed to flush Mixpanel queue', flushError);
      }
    }
  }
};

// Identify user with Simplified ID Merge
export const identifyUser = (userId: string, properties?: Record<string, unknown>) => {
  try {
    if (!isMixpanelReady()) {
      return;
    }
    const mp = getMixpanel();
    if (mp) {
      // Get the current device ID before identification
      const deviceId = mp.get_distinct_id();
      logInfo('Identifying user', { deviceId, userId });
      
      // Call identify() to set $user_id for future events
      // This will create the ID merge between $device_id and $user_id
      mp.identify(userId);
      
      const previousStatus = getStoredUserStatus() || DEFAULT_USER_STATUS;
      setStoredUserStatus('registered');

      const peopleProps = {
        ...(properties || {}),
        user_status: 'registered',
      };

      mp.people.set(peopleProps);
      
      // Set user ID as a super property for all future events
      mp.register({
        $user_id: userId,
        user_id: userId,
        // Store the device ID for reference
        $device_id: deviceId,
        user_status: 'registered',
      });
      
      // Send a test event to trigger the ID merge
      mp.track('User Identified', withCampaignProps({
        $user_id: userId,
        $device_id: deviceId,
        identification_method: 'simplified_id_merge',
        previous_status: previousStatus,
        new_status: 'registered',
        ...properties
      }));

      mp.track('User Status Updated', withCampaignProps({
        previous_status: previousStatus,
        new_status: 'registered',
        user_id: userId,
      }));
      
      logInfo('User identified successfully with Simplified ID Merge', { userId });
    }
  } catch (error) {
    logError('Failed to identify user', error);
  }
};

// Set user properties
export const setUserProperties = (properties: Record<string, unknown>) => {
  try {
    if (!isMixpanelReady()) {
      return;
    }
    const mp = getMixpanel();
    if (mp) {
      mp.people.set(properties);
    }
  } catch (error) {
    logError('Failed to set user properties', error);
  }
};

// Reset user (for logout)
export const resetUser = () => {
  try {
    if (!isMixpanelReady()) {
      return;
    }
    const mp = getMixpanel();
    if (mp) {
      const previousStatus = getStoredUserStatus() || DEFAULT_USER_STATUS;
      mp.reset();
      setStoredUserStatus(DEFAULT_USER_STATUS);
      mp.register({ user_status: DEFAULT_USER_STATUS });

      if (typeof mp.track === 'function') {
        mp.track('User Status Updated', withCampaignProps({
          previous_status: previousStatus,
          new_status: DEFAULT_USER_STATUS,
          reason: 'manual_reset',
        }));
      }
      logInfo('User reset successfully');
    }
  } catch (error) {
    logError('Failed to reset user', error);
  }
};

// Get current user ID
export const getCurrentUserId = () => {
  try {
    if (!isMixpanelReady()) {
      return null;
    }
    const mp = getMixpanel();
    if (mp) {
      return mp.get_distinct_id();
    }
  } catch (error) {
    logError('Failed to get current user ID', error);
  }
  return null;
};

// Get current device ID (for debugging)
export const getCurrentDeviceId = () => {
  try {
    if (!isMixpanelReady()) {
      return null;
    }
    const mp = getMixpanel();
    if (mp) {
      return (mp.get_property('$device_id') as string | undefined) || mp.get_distinct_id() || null;
    }
  } catch (error) {
    logError('Failed to get current device ID', error);
  }
  return null;
};

// Get ID merge status (for debugging)
export const getIDMergeStatus = () => {
  try {
    if (!isMixpanelReady()) {
      return null;
    }
    const mp = getMixpanel();
    if (mp) {
      const distinctId = mp.get_distinct_id();
      const superProps = (mp.get_property('$super_properties') as Record<string, unknown> | undefined) || {};
      
      return {
        distinct_id: distinctId,
        $user_id: superProps.$user_id,
        $device_id: superProps.$device_id,
        is_identified: Boolean(superProps.$user_id),
        id_merge_active: Boolean(superProps.$user_id && superProps.$device_id)
      };
    }
  } catch (error) {
    logError('Failed to get ID merge status', error);
  }
  return null;
};

// Manual queue flush to prevent mutex lock timeouts
export const flushMixpanelQueue = () => {
  try {
    const mp = getMixpanel();
    if (mp && typeof mp === 'object' && 'flush' in mp) {
      (mp as { flush?: () => void }).flush?.();
      logInfo('Mixpanel queue flushed manually');
    }
  } catch (error) {
    logError('Failed to flush Mixpanel queue', error);
  }
};
